import { useState } from "preact/hooks"
import useSWR from "swr"
import { api_fetch_throws } from "../../../api/client"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs"
import { day_label, month_label } from "./format"
import { InventoryTab } from "./inventory_tab"
import { ProjectionsTab } from "./projections_tab"
import { DistributionTab } from "./distribution_tab"
import { StoresTab } from "./stores_tab"
import type { DistributionView, InventoryView, ProjectionView } from "./types"

const TABS: { value: string; label: string }[] = [
  { value: "inventory", label: "DC Inventory" },
  { value: "projections", label: "Order Projections" },
  { value: "distribution", label: "Distribution & Voids" },
  { value: "stores", label: "Stores" },
]

function useSupply<T>(path: string) {
  return useSWR([`kehe-supply`, path], () => api_fetch_throws<T>(`/distributor-supply/${path}?source=kehe`), { revalidateOnFocus: false })
}

/** When each feed was last read, so a stale one is visible. */
function Freshness(props: { inventory?: InventoryView; projections?: ProjectionView; distribution?: DistributionView }) {
  const parts: string[] = []
  if (props.inventory?.snapshot_date) parts.push(`inventory ${day_label(props.inventory.snapshot_date)}`)
  if (props.projections?.as_of_date) parts.push(`projections ${day_label(props.projections.as_of_date)}`)
  const months = props.distribution?.months ?? []
  if (months.length) parts.push(`store volume through ${month_label(months[months.length - 1].month)}`)
  if (!parts.length) return null
  return <p class="text-xs text-gray-500">From KeHE CONNECT: {parts.join(" · ")}</p>
}

/**
 * KeHE's view of the brand's supply and stores, pulled daily by `sync-kehe-reports`: what each DC
 * holds and has on order, what KeHE expects to order, which stores receive what, and the store list.
 */
export function KeheSupplySection({ className }: { className?: string }) {
  const [tab, set_tab] = useState("inventory")
  const inventory = useSupply<InventoryView>("inventory")
  const projections = useSupply<ProjectionView>("projections")
  const distribution = useSupply<DistributionView>("distribution")
  const loading = inventory.isLoading || projections.isLoading || distribution.isLoading
  const failed = inventory.error || projections.error || distribution.error
  return (
    <div class={`space-y-4 ${className ?? ""}`}>
      <div>
        <h2 class="text-lg font-semibold text-gray-900">KeHE supply</h2>
        <p class="mt-1 text-sm text-gray-600">
          DC inventory, KeHE's order projections and store-level distribution, pulled daily from KeHE CONNECT.
        </p>
        <Freshness inventory={inventory.data} projections={projections.data} distribution={distribution.data} />
      </div>
      {failed ? <p class="text-sm text-red-600">Could not load KeHE supply data: {String(failed?.message ?? failed)}</p> : null}
      {loading ? <p class="text-sm text-gray-500">Loading…</p> : null}
      <Tabs value={tab} onValueChange={set_tab}>
        <TabsList className="h-auto flex-wrap">
          {TABS.map((item: { value: string; label: string }) => (
            <TabsTrigger key={item.value} value={item.value}>
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="inventory">{inventory.data ? <InventoryTab data={inventory.data} /> : null}</TabsContent>
        <TabsContent value="projections">{projections.data ? <ProjectionsTab data={projections.data} /> : null}</TabsContent>
        <TabsContent value="distribution">{distribution.data ? <DistributionTab data={distribution.data} /> : null}</TabsContent>
        <TabsContent value="stores">{tab === "stores" ? <StoresTab /> : null}</TabsContent>
      </Tabs>
    </div>
  )
}
